/**
 * BreakPeriodCard Component
 * 
 * Requirement 8.5: Break period display
 * 
 * Displays break periods (short break, lunch, recess) with distinct styling
 * from regular period cards.
 */

import { Box, Paper, Typography, styled } from '@mui/material';
import { FreeBreakfast as BreakIcon, Restaurant as LunchIcon, SportsBasketball as RecessIcon } from '@mui/icons-material';
import type { SchedulePeriod } from '@timetable/types';

const BreakCardContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  cursor: 'default',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  borderRadius: '6px',
  minHeight: '52px',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: '#fffbeb',
  border: '2px solid #fde68a',
  '&:hover': {
    backgroundColor: '#fef3c7',
  },
}));

interface BreakPeriodCardProps {
  period: SchedulePeriod;
}

export function BreakPeriodCard({ period }: BreakPeriodCardProps) {
  // Determine icon based on break type
  const getBreakIcon = () => {
    switch (period.break_type) {
      case 'lunch':
        return <LunchIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'recess':
        return <RecessIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
      case 'short':
      default:
        return <BreakIcon sx={{ fontSize: 16, color: 'warning.main' }} />;
    }
  };

  // Determine label based on break type
  const getBreakLabel = () => {
    switch (period.break_type) {
      case 'lunch':
        return 'Lunch Break';
      case 'recess':
        return 'Recess';
      case 'short':
        return 'Short Break';
      default:
        return 'Break';
    }
  };

  return (
    <BreakCardContainer
      elevation={1}
      data-testid="break-period-card"
      data-period-id={period.id}
      role="presentation"
      aria-label={`${getBreakLabel()} from ${period.time_start} to ${period.time_end}`}
    >
      {getBreakIcon()}
      <Typography
        variant="caption"
        fontWeight={600}
        fontSize="12px"
        color="warning.dark"
        textAlign="center"
      >
        {period.period_name || getBreakLabel()}
      </Typography>
    </BreakCardContainer>
  );
}
